import { AudioLines, Images, type LucideIcon, ScanFace, Sparkles } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

export type IdentityDetailTab =
  | 'faces-photos'
  | 'possible-matches'
  | 'overview'
  | 'audio-records-voice'

/** The Advanced Face Detection parts of the card (ADR-0051), each in its own tab. */
export interface IdentityFaceSections {
  faces: ReactNode
  photos?: ReactNode
  photosAction?: ReactNode
  possibleMatches?: ReactNode
}

export interface IdentityDetailLayoutProps {
  header: ReactNode
  overview: ReactNode
  /** Extension-owned media tabs, independently available. */
  media?: { faces?: IdentityFaceSections; audio?: ReactNode }
  defaultTab?: IdentityDetailTab
}

const BASE = 'identity.detail.sections'

function Placeholder({ text }: { text: string }) {
  return (
    <Card variant="dashed">
      <CardContent className="py-8 text-center text-sm text-muted-foreground">{text}</CardContent>
    </Card>
  )
}

function OverviewSection({ overview }: { overview: ReactNode }) {
  const { t } = useTranslation()
  return (
    <>
      <h2 className="text-lg font-semibold text-foreground">{t(`${BASE}.profileDocuments`)}</h2>
      {overview}
    </>
  )
}

interface IdentityTabItem {
  value: IdentityDetailTab
  label: string
  Icon: LucideIcon
}

function identityTabs(media: IdentityDetailLayoutProps['media']): IdentityTabItem[] {
  const tabs: IdentityTabItem[] = [{ value: 'overview', label: 'profileDocuments', Icon: ScanFace }]
  if (media?.faces)
    tabs.push(
      { value: 'faces-photos', label: 'facesAndPhotos', Icon: Images },
      { value: 'possible-matches', label: 'possibleMatches', Icon: Sparkles }
    )
  if (media?.audio)
    tabs.push({ value: 'audio-records-voice', label: 'audioRecordsAndVoice', Icon: AudioLines })
  return tabs
}

function IdentityTabNavigation({ items }: { items: IdentityTabItem[] }) {
  const { t } = useTranslation()
  return (
    <div className="overflow-x-auto border-b border-border">
      <TabsList variant="line" aria-label={t(`${BASE}.navLabel`)}>
        {items.map(({ value, label, Icon }) => (
          <TabsTrigger key={value} value={value} className="gap-2 px-2 py-2.5 sm:px-3">
            <Icon className="hidden size-4 sm:block" />
            {t(`${BASE}.${label}`)}
          </TabsTrigger>
        ))}
      </TabsList>
    </div>
  )
}

/** The profile stays in view while the working area changes between evidence and review. */
export function IdentityDetailLayout({
  header,
  overview,
  media,
  defaultTab = 'overview',
}: IdentityDetailLayoutProps) {
  const tabs = identityTabs(media)
  const faceSections = media?.faces
  const audioSection = media?.audio
  if (!faceSections && !audioSection) {
    return (
      <div className="flex flex-col gap-6">
        {header}
        <section className="space-y-4">
          <OverviewSection overview={overview} />
        </section>
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-6">
      {header}
      <Tabs
        defaultValue={tabs.some(tab => tab.value === defaultTab) ? defaultTab : 'overview'}
        className="gap-6"
      >
        <IdentityTabNavigation items={tabs} />
        <TabsContent value="overview" className="space-y-4">
          <OverviewSection overview={overview} />
        </TabsContent>
        {faceSections && <FaceTabs sections={faceSections} />}
        {audioSection && <TabsContent value="audio-records-voice">{audioSection}</TabsContent>}
      </Tabs>
    </div>
  )
}

function FaceTabs({ sections }: { sections: IdentityFaceSections }) {
  const { t } = useTranslation()
  return (
    <>
      <TabsContent value="faces-photos" className="space-y-7">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{t(`${BASE}.facesAndPhotos`)}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t(`${BASE}.facesHint`)}</p>
        </div>
        <section aria-label={t(`${BASE}.faces`)}>{sections.faces}</section>
        <section aria-labelledby="identity-photos-heading" className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 id="identity-photos-heading" className="text-lg font-semibold text-foreground">
                {t(`${BASE}.photos`)}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">{t(`${BASE}.photosHint`)}</p>
            </div>
            {sections.photosAction}
          </div>
          {sections.photos ?? <Placeholder text={t(`${BASE}.photosEmpty`)} />}
        </section>
      </TabsContent>
      <TabsContent value="possible-matches" className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{t(`${BASE}.possibleMatches`)}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t(`${BASE}.matchesHint`)}</p>
        </div>
        {sections.possibleMatches ?? <Placeholder text={t(`${BASE}.possibleMatchesEmpty`)} />}
      </TabsContent>
    </>
  )
}
